import React, { useContext } from "react";
import { Button } from "react-bootstrap";
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { GetShippingLabel } from "../helper/apiHelper";
import { useSelector } from "react-redux";
import { SearchContext } from "../helper/context/searchContext";

// Concordance des références : fichier client/public/concordance.jsonl (export GTIN),
// une ligne JSON par déclinaison (pointure) :
// {"gtin_p": "...", "code$": "...", "coderef": "...", "codereflib": "...", "eans": "...", "refs": "..."}
// Clé de recherche : coderef = la colonne « Référence du produit » du tableau de picking.
// Seul le code$ correspondant est affiché sous la référence du produit.
// Fichier volumineux (~60 Mo / 417 000 lignes) : chargé et indexé une seule fois par session.
let concordancePromise = null;
const loadConcordance = () => {
    if (!concordancePromise) {
        concordancePromise = (async () => {
            const map = new Map();
            try {
                const res = await fetch(`${process.env.PUBLIC_URL}/concordance.jsonl`);
                if (!res.ok) return map;
                const text = await res.text();
                text.split(/\r?\n/).forEach((line) => {
                    try {
                        const o = JSON.parse(line);
                        if (!o.coderef) return;
                        const alts = map.get(o.coderef) || [];
                        const codeDollar = o['code$'];
                        if (typeof codeDollar === 'string' && codeDollar.trim() && !alts.includes(codeDollar)) {
                            alts.push(codeDollar);
                        }
                        map.set(o.coderef, alts);
                    } catch (e) {
                        // ligne vide ou non-JSON : ignorée
                    }
                });
            } catch (e) {
                // fichier absent ou injoignable : le PDF sort sans références supplémentaires
            }
            return map;
        })();
    }
    return concordancePromise;
};

const PdfGenerator = ({ currentTab}) => {
    const selectedRows = useSelector(
        (state) => state.generalData.orderSelectedRows
    );
    const userId = useSelector(
        (state) => state.auth.userId
    )
    const { toggleLoader } = useContext(SearchContext);
    const generatePDF = async () => {
        toggleLoader(true);
        if(currentTab === "CCV" || currentTab === "CCVZalando") {
            const response = await GetShippingLabel(currentTab,
                "test_picking.php",
                [{ checkbox: selectedRows.toString(), type: "0", employee_id: userId }]
            );
            const orders = response.data.orders;
            const dateSession = response.data.date_session;
            const hasLocation = response.data.hasLocation;
            const concordance = await loadConcordance();
            const ps15x = true;
            
            const doc = new jsPDF({
                orientation: 'landscape',
                unit: 'px',
                format: 'a4',
            });
        
            doc.text(`Commandes ${dateSession}`, 20, 20);
        
            const tableColumn = [
                'SITE',
                'ID',
                'Référence',
                'Image',
                'Référence du produit',
                ...(hasLocation ? ['Emplacement'] : []),
                'Taille',
                'Secteur',
                'Client',
                'Adresse',
                'Transporteur',
                'Nom du produit',
                'Qté',
                'Saison',
                "Prix d'origine",
                'Prix de vente',
                'Poids',
                'Cadeaux',
                'Commentaire',
            ];
        
            const tableRows = [];
            let sitename = currentTab === "CCVZalando" ? "CCV" : currentTab;
        
            orders.forEach((order) => {
                const productQuantity = parseInt(order[9], 10);
                // « Référence du produit » (order[3]) = coderef du fichier de concordance
                const altRefs = concordance.get(order[3]) || [];
                const productRefCell = altRefs.length ? `${order[3]}\n${altRefs.join('\n')}` : order[3];

                for (let i = 1; i <= productQuantity; i++) {
                    const orderRow = [
                        sitename,
                        i === 1 ? order[0] : '',
                        order[1],
                        { imageUrl: order[2], width: 45, height: 45 },
                        productRefCell,
                        ...(hasLocation ? [order[15]] : []),
                        order[4],
                        order[5],
                        order[6],
                        order[7],
                        order[16],
                        order[8],
                        { content: '1', styles: { fontStyle: 'bold', textColor: order[9] > 1 ? 'red' : 'black' } },
                        order[12],
                        order[13],
                        { content: order[14], styles: { textColor: 'red' } },
                        order[10],
                        order[11] === 1 ? 'OUI' : '',
                        '',
                    ];
        
                    tableRows.push(orderRow);
                }
            });
        
            doc.autoTable({
                head: [tableColumn],
                body: tableRows,
                rowPageBreak: 'avoid',
                styles: {
                    fontSize: 8,
                    textColor: 'black',
                    lineWidth: 0.5,
                    lineColor: 'black',
                },
                headStyles: {
                    fillColor: '#FFFACD',  
                    textColor: 'black',
                    lineWidth: 0.5,
                    lineColor: 'black',
                },
                columnStyles: {
                    0: { halign: 'center', cellWidth: 25 },
                    1: { halign: 'center', cellWidth: 25 },
                    2: { cellWidth: 30 }, // Référence
                    3: { halign: 'center', cellWidth: 60 }, // Image
                    5: { cellWidth: hasLocation ? 20 : 25 },
                    ...(hasLocation ? {6: {cellWidth: 20}} : {}),
                    8: { cellWidth: hasLocation ? '' : 30 },
                    9: { cellWidth: hasLocation ? 30 : 40 },
                    ...(hasLocation ? {10: {cellWidth: 40}} : {}),
                    12: { cellWidth: hasLocation ? '' : 30 },
                    ...(hasLocation ? {13: {cellWidth: 30}} : {}),
                    14: { cellWidth: hasLocation ? '' : 25 },
                    ...(hasLocation ? {15: {cellWidth: 25}} : {}),
                    ...(hasLocation ? {17: {cellWidth: 20}} : {}),
                    ...(hasLocation ? {18: {cellWidth: 20}} : {}),
                },
                didDrawCell: (data) => {
                    const { column, cell } = data;
                    if (column.index === 3 && cell.raw?.imageUrl) {
                        doc.addImage(cell.raw.imageUrl, 'JPEG', cell.x + 4, cell.y + 2, cell.raw.width, cell.raw.height);
                    }
                },
            });
        
            doc.save(`ListingOrder_${Date.now()}.pdf`);
        } else {
            const response = await GetShippingLabel(currentTab,
                "download_product_data.php",
                [{ checkbox: selectedRows.toString(), type: "0", employee_id: userId }]
            );
            const fileURL = response.data;
            window.open(fileURL, "PRINT");
        }
        toggleLoader(false);
    };

    return (
        <div>
            <Button onClick={generatePDF}>Télécharger le tableau de picking</Button>
        </div>
    );
};

export default PdfGenerator;
